import { useMemo } from 'react';
import { Bar, BarProps, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';
import { useThemeValues } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';
import { assertNumber } from 'utils/assert';

import { StyledBarChart } from './styled';

/**
 * Configuration for a single segment in the stacked bar chart.
 * Multiple segments with the same stackId are stacked horizontally.
 */
export type SegmentConfig<
    TData,
    TKey extends string & keyof TData = string & keyof TData
> = {
    dataKey: TKey;
    label: string;
    color: string;
    stackId: string;
};

type Props<
    TData extends Record<string, unknown>,
    TCategoryKey extends string & keyof TData
> = Readonly<{
    data: TData[];
    segments: SegmentConfig<TData>[];
    /** Key for Y-axis category labels */
    categoryKey: TCategoryKey;
    yAxisWidth?: number;
    xAxisHeight?: number;
    minBarHeight?: string;
    /** Spacing between bar groups (not segments within a stack) */
    barCategoryGap?: number;
    showGrid?: boolean;
    showTooltip?: boolean;
    /** Show numeric value labels centered in each bar segment */
    showBarLabels?: boolean;
    /** Horizontal gap in pixels between segments within a stack */
    stackGap?: 0 | 1 | 2 | 4 | 8;
    /** Corner rounding: 'none', 'all' bars, or 'last-item' (last visible segment only) */
    rounding?: 'all' | 'none' | 'last-item';
    cornerRadius?: number;
    /** Minimum value required to display a label on a bar segment */
    labelMinValue?: number;
}>;

const BarsContainer = styled.div`
    .recharts-layer .recharts-label-list text.recharts-text {
        fill: ${KCFColor.primaryText};
        font-weight: 600;
    }
`;

/**
 * Creates an SVG path for a rectangle with optional rounded corners on the right side.
 * @param x - X coordinate of the top-left corner
 * @param y - Y coordinate of the top-left corner
 * @param width - Width of the rectangle
 * @param height - Height of the rectangle
 * @param radius - Radius for the rounded corners (default: 5)
 * @returns SVG path string
 */
function createRightRoundedRectPath(
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number = 5
): string {
    // M = move to
    // L = line to
    // Q = quadratic bezier curve: start (current position), control point, end point (https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/d#quadratic_b%C3%A9zier_curve)
    // Z = close shape

    // M  top-left
    // L top right inner
    // Q curve to top far-right
    // L line to bottom far-right
    // Q curve to bottom right
    // L line to bottom left
    // Z close shape with starting point
    return `
        M ${x} ${y}
        L ${x + width - radius} ${y}
        Q ${x + width} ${y}, ${x + width} ${y + radius}
        L ${x + width} ${y + height - radius}
        Q ${x + width} ${y + height}, ${x + width - radius} ${y + height}
        L ${x} ${y + height}
        Z
    `;
}

/**
 * Creates label configuration for stacked bars
 */
function createStackedBarLabelConfig(labelMinValue: number) {
    return {
        position: 'center' as const,
        valueAccessor: ({ value }: { value: unknown }) => {
            if (typeof value === 'number') {
                return value;
            } else if (
                Array.isArray(value) &&
                typeof value[0] === 'number' &&
                typeof value[1] === 'number'
            ) {
                return value[1] - value[0];
            }
            return Number(value);
        },
        formatter: (item: unknown): number | undefined =>
            Number(item) >= labelMinValue ? Number(item) : undefined,
        fontSize: 11,
    };
}

/**
 * Determines if a bar segment should have rounded corners based on rounding mode
 */
function shouldRoundBarSegment<TData extends Record<string, unknown>>(
    rounding: 'all' | 'none' | 'last-item',
    data: TData[],
    segments: SegmentConfig<TData>[],
    idxInStack: number,
    dataIndex: number | undefined
): boolean {
    switch (rounding) {
        case 'none':
            return false;
        case 'all':
            return true;
        case 'last-item': {
            // rounding === 'last-item'
            // Determine if this is the last visible bar in the stack
            const rowData = typeof dataIndex === 'number' ? data[dataIndex] : undefined;
            const isLastBar = idxInStack === segments.length - 1;

            if (isLastBar) {
                return true;
            }

            if (!rowData) {
                return false;
            }

            // if anything to the right of this one has data, this is not last
            for (let i = idxInStack + 1; i < segments.length; i++) {
                const key = segments[i].dataKey;
                if (rowData[key]) {
                    return false;
                }
            }

            return true;
        }
    }
}

/**
 * Creates shape renderer for stacked bars with optional right-rounded corners
 * and stack gaps
 */
function createStackedBarShapeRenderer<TData extends Record<string, unknown>>(
    data: TData[],
    segments: SegmentConfig<TData>[],
    idxInStack: number,
    options: {
        rounding: 'all' | 'none' | 'last-item';
        useStackGap: boolean;
        stackGap: number;
        cornerRadius: number;
    }
) {
    return (props: BarProps) => {
        const { fill, x, y, width, height, index } = props;
        assertNumber(x);
        assertNumber(width);
        assertNumber(y);
        assertNumber(height);

        // don't render anything for zero values
        if (width === 0) {
            return <></>;
        }

        const adjustedX = options.useStackGap ? x + options.stackGap / 2 : x;
        const adjustedWidth = options.useStackGap ? width - options.stackGap : width;

        const shouldRound = shouldRoundBarSegment(
            options.rounding,
            data,
            segments,
            idxInStack,
            Number(index)
        );

        if (shouldRound) {
            const path = createRightRoundedRectPath(
                adjustedX,
                y,
                adjustedWidth,
                height,
                options.cornerRadius
            );
            return <path d={path} fill={fill} />;
        }

        // for non-rounded segments, use simple rect
        return (
            <rect fill={fill} x={adjustedX} y={y} width={adjustedWidth} height={height} />
        );
    };
}

export function StackedHorizontalBarChart<
    TData extends Record<string, unknown>,
    TCategoryKey extends string & keyof TData
>({
    data,
    segments,
    categoryKey,
    yAxisWidth = 90,
    xAxisHeight = 30,
    barCategoryGap = 4,
    showGrid = true,
    showTooltip = true,
    showBarLabels = false,
    stackGap = 0,
    rounding = 'none',
    cornerRadius = 5,
    labelMinValue = 1,
}: Props<TData, TCategoryKey>) {
    const theme = useThemeValues();

    const useRoundedCorners = rounding !== 'none';
    const useStackGap = stackGap > 0;

    const labelConfig = useMemo(
        () => (showBarLabels ? createStackedBarLabelConfig(labelMinValue) : undefined),
        [showBarLabels, labelMinValue]
    );

    return (
        <BarsContainer>
            <StyledBarChart data={data} layout="vertical" barCategoryGap={barCategoryGap}>
                {showGrid && (
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.borderColor}
                        horizontal={false}
                    />
                )}
                <XAxis
                    type="number"
                    tick={{ fill: theme.primaryText }}
                    height={xAxisHeight}
                />
                <YAxis
                    type="category"
                    dataKey={categoryKey}
                    tick={{ fill: theme.primaryText }}
                    width={yAxisWidth}
                />
                {showTooltip && (
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme.backgroundColor,
                            border: `1px solid ${theme.borderColor}`,
                        }}
                        labelStyle={{ color: theme.primaryText }}
                    />
                )}
                {segments.map((segment, idxInStack) => {
                    const shapeRenderer =
                        useRoundedCorners || useStackGap
                            ? createStackedBarShapeRenderer(data, segments, idxInStack, {
                                  rounding,
                                  useStackGap,
                                  stackGap,
                                  cornerRadius,
                              })
                            : undefined;

                    return (
                        <Bar
                            key={segment.dataKey}
                            dataKey={segment.dataKey}
                            stackId={segment.stackId}
                            fill={segment.color}
                            name={segment.label}
                            label={labelConfig}
                            shape={shapeRenderer}
                        />
                    );
                })}
            </StyledBarChart>
        </BarsContainer>
    );
}
